import React, { useMemo } from "react";
import * as d3 from "d3";

const WIDTH = 320;
const HEIGHT = 260;

const MARGIN = {
  top: 42,
  right: 24,
  bottom: 70,
  left: 24,
};

const INNER_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const INNER_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

const STROKE_WIDTH = 8;

// These define the FIXED visual daylight arc on the curve
// (do not change these unless the design changes)
const VISUAL_SUNRISE_X = 6;
const VISUAL_SUNSET_X = 18;

const parseTimeToHour = (timeText) => {
  if (!timeText || typeof timeText !== "string") {
    return null;
  }

  const match = timeText.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase();

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }

  let normalizedHour = hour % 12;
  if (period === "PM") {
    normalizedHour += 12;
  }

  return normalizedHour + minute / 60;
};

const splitTimeLabel = (timeText, fallbackTime, fallbackPeriod) => {
  if (!timeText || typeof timeText !== "string") {
    return { time: fallbackTime, period: fallbackPeriod };
  }

  const match = timeText.match(/^(\d{1,2}:\d{2})\s*([AP]M)$/i);
  if (!match) {
    return { time: fallbackTime, period: fallbackPeriod };
  }

  return { time: match[1], period: match[2].toUpperCase() };
};

export function CurrentIrradiance({
  currentHour,
  sunrise = "",
  sunset = "",
  directNormalIrradiance = null,
  dateLabel = "",
}) {
  const effectiveSunriseHour = parseTimeToHour(sunrise) ?? 6;
  const effectiveSunsetHour = parseTimeToHour(sunset) ?? 18;

  const sunriseLabel = splitTimeLabel(sunrise, "6:00", "AM");
  const sunsetLabel = splitTimeLabel(sunset, "6:00", "PM");

  const durationHours =
    effectiveSunsetHour > effectiveSunriseHour
      ? Number((effectiveSunsetHour - effectiveSunriseHour).toFixed(2))
      : null;

  /* X scale: fixed 24h visual domain */
  const xScale = useMemo(
    () => d3.scaleLinear().domain([0, 24]).range([0, INNER_WIDTH]),
    []
  );

  /* Visual center */
  const horizonY = Math.round(INNER_HEIGHT / 2) + 0.5;

  /* Fixed amplitude (never changes) */
  const AMPLITUDE = (INNER_HEIGHT - STROKE_WIDTH) / 2;

  /* ✅ Static, symmetric cosine path */
  const sunCurve = (x) =>
    horizonY -
    AMPLITUDE *
      Math.cos(((x - 12) / 24) * Math.PI * 2);

  const line = d3
    .line()
    .x(d => xScale(d))
    .y(d => sunCurve(d))
    .curve(d3.curveCatmullRom.alpha(0.6));

  /* Static paths */
  const hours = d3.range(0, 24.01);
  const daylight = d3.range(
    VISUAL_SUNRISE_X,
    VISUAL_SUNSET_X + 0.01
  );

  /* ------------------------------------
     ✅ CORRECT SUN POSITION MAPPING
     ------------------------------------ */

  // Clamp current time to real daylight window
  const clampedHour = Math.min(
    effectiveSunsetHour,
    Math.max(effectiveSunriseHour, currentHour)
  );

  // Progress through REAL daylight [0 → 1]
  const daylightProgress =
    (clampedHour - effectiveSunriseHour) /
    (effectiveSunsetHour - effectiveSunriseHour || 1);

  // Map that progress onto the FIXED daylight arc
  const sunCurveX =
    VISUAL_SUNRISE_X +
    daylightProgress *
      (VISUAL_SUNSET_X - VISUAL_SUNRISE_X);

  const sunX = xScale(sunCurveX);
  const sunY = sunCurve(sunCurveX);

  /* Static intersection points */
  const sunriseX = xScale(VISUAL_SUNRISE_X);
  const sunsetX = xScale(VISUAL_SUNSET_X);

  return (
    <div className="panel component mb-5">
      <svg width={WIDTH} height={HEIGHT}>
        <defs>
          <linearGradient
            id="sunGradient"
            gradientUnits="userSpaceOnUse"
            x1={sunriseX}
            x2={sunsetX}
          >
            <stop offset="0%" stopColor="#F58025" />
            <stop offset="50%" stopColor="#C0379A" />
            <stop offset="100%" stopColor="#4B2FFF" />
          </linearGradient>
        </defs>

        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          <text
            x={INNER_WIDTH / 2}
            y={-18}
            textAnchor="middle"
            fontWeight="700"
            fontSize="18"
          >
            TODAY’S SUN STATUS
          </text>
          <text
            x={INNER_WIDTH / 2}
            y={150}
            textAnchor="middle"
            fontWeight="700"
            fontSize="18"
          >
            {dateLabel}
          </text>

          {/* Horizon */}
          <line
            x1={0}
            x2={INNER_WIDTH}
            y1={horizonY}
            y2={horizonY}
            stroke="#CFCFCF"
          />

          {/* Full static sun path */}
          <path
            d={line(hours)}
            fill="none"
            stroke="#D8D8D8"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />

          {/* Static daylight highlight */}
          <path
            d={line(daylight)}
            fill="none"
            stroke="url(#sunGradient)"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />

          {/* Sunrise / sunset dots (static) */}
          {[sunriseX, sunsetX].map((x, i) => (
            <g key={i}>
              <circle
                cx={x}
                cy={horizonY}
                r={5}
                fill="#E0E0E0"
                stroke="#FFF"
                strokeWidth="4"
              />
            </g>
          ))}

          {/* ✅ Current sun (only moving element) */}
          <circle
            cx={sunX}
            cy={sunY}
            r={7}
            fill="#000"
            stroke="#FFF"
            strokeWidth="4"
          />

          {directNormalIrradiance != null && (
            <text
              x={INNER_WIDTH / 2}
              y={horizonY - AMPLITUDE * 0.2}
              textAnchor="middle"
              fontWeight="700"
            >
              {directNormalIrradiance} W/m²
            </text>
          )}

          <text
            x={INNER_WIDTH / 2}
            y={horizonY + 36}
            textAnchor="middle"
            fontWeight="600"
          >
            {durationHours != null ? `${durationHours} hrs` : ""}
          </text>

          {/* Sunrise label */}
          <text
            x={0}
            y={INNER_HEIGHT + 36}
            fontSize="22"
            fontWeight="700"
          >
            {sunriseLabel.time}
            <tspan fontSize="12" dx="3">
              {sunriseLabel.period}
            </tspan>
          </text>
          <text x={0} y={INNER_HEIGHT + 52} fontSize="12">
            Sunrise
          </text>

          {/* Sunset label */}
          <text
            x={INNER_WIDTH}
            y={INNER_HEIGHT + 36}
            textAnchor="end"
            fontSize="22"
            fontWeight="700"
          >
            {sunsetLabel.time}
            <tspan fontSize="12" dx="3">
              {sunsetLabel.period}
            </tspan>
          </text>
          <text
            x={INNER_WIDTH}
            y={INNER_HEIGHT + 52}
            textAnchor="end"
            fontSize="12"
          >
            Sunset
          </text>
        </g>
      </svg>
    </div>
  );
}